import { memo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BlockRenderer } from '@realsite/blocks';
import { getTemplatePreview } from '@/api/templates';

/** Minimal shape needed to render a preview — satisfied by both `Template` (api/templates) and
 *  `RootTemplate` (api/root), so the admin gallery + wizard picker share one renderer. */
export interface PreviewableTemplate {
  slug: string;
  name: string;
  preview_url?: string | null;
  thumbnail_url?: string | null;
}

/**
 * A REAL, scaled-down render of a template's actual home page (its blocks + palette) — the same
 * engine the live site uses — so a card shows the actual website, not a stale stock screenshot.
 * Falls back to a hosted preview_url/thumbnail, then null (the caller's base layer shows through).
 *
 * SHARED so the dashboard template gallery AND the setup-wizard template picker render templates
 * IDENTICALLY — they used to differ (dashboard = live render, wizard = static thumbnail_url), so a
 * redesigned template showed a stale screenshot in the wizard. One source now → they can't drift.
 */
const PreviewRenderer = memo(BlockRenderer);
const PREVIEW_DATA = { listings: [] };

/** Map a template's saved style to the CSS vars the block renderer + tenant layout read, so a
 *  live preview shows the template's real palette + fonts. */
export function templateStyleToVars(style: Record<string, unknown> | null | undefined): CSSProperties {
  const s = (style ?? {}) as Record<string, string | undefined>;
  const v: Record<string, string> = {};
  if (s.color_primary) v['--color-primary'] = s.color_primary;
  if (s.color_secondary) v['--color-secondary'] = s.color_secondary;
  if (s.color_accent) v['--color-accent'] = s.color_accent;
  if (s.color_background) v['--color-background'] = s.color_background;
  if (s.color_surface) v['--color-surface'] = s.color_surface;
  if (s.color_text) v['--color-text'] = s.color_text;
  if (s.color_text_muted) v['--color-text-muted'] = s.color_text_muted;
  if (s.heading_font) v['--font-heading'] = s.heading_font;
  if (s.body_font) v['--font-body'] = s.body_font;
  if (s.hero_image_url) v['--hero-image'] = `url("${String(s.hero_image_url).replace(/["\\\n\r]/g, '')}")`;
  return v as CSSProperties;
}

/** Renders into an absolutely-positioned layer — place it inside a `relative overflow-hidden`
 *  parent (typically `pointer-events-none absolute inset-0`). Returns null when there's nothing to
 *  show, so the parent's own base/wireframe layer shows through. */
export function TemplateLivePreview({ template }: { template: PreviewableTemplate }) {
  const [imgFailed, setImgFailed] = useState(false);
  const { data } = useQuery({
    queryKey: ['template-preview', template.slug],
    queryFn: () => getTemplatePreview(template.slug),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  if (data?.blocks && data.blocks.length > 0) {
    return (
      <div
        className="absolute left-0 top-0 h-[250%] w-[250%] origin-top-left scale-[0.4] overflow-hidden bg-white"
        style={{ ...templateStyleToVars(data.style), contentVisibility: 'auto' } as CSSProperties}
      >
        <PreviewRenderer blocks={data.blocks} data={PREVIEW_DATA} />
      </div>
    );
  }
  const img = data?.preview_url || template.preview_url || template.thumbnail_url;
  if (img && !imgFailed) {
    return (
      <img
        src={img}
        alt={template.name}
        onError={() => setImgFailed(true)}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
    );
  }
  return null;
}

export default TemplateLivePreview;
